
import Vue from 'vue';
import { Data as HeadlinesData, DataWithHref, QueryResult } from '@root/modules/headlines/types/headlines';
import getHeadlineWithHref from '@root/modules/headlines/utils/getHeadlineWithHref';
import HeadlineBlocks from '@root/common/components/base/headline/HeadlineBlocks.vue';
import ArticlesCounter from '@root/modules/article/components/multimedija/ArticlesCounter.vue';
import ArticleHandler from '@root/modules/article/components/multimedija/ArticleHandler.vue';

import Modal from '@core/components/UI/Modal.vue';
import UniversalHeadlinesService from '@root/modules/headlines/services/UniversalHeadlines.service';

interface Methods {
  getHeadline: (headline: HeadlinesData) => DataWithHref;
  handleScroll: () => void;
  updateFitler: (tagId: number) => void;
  updateHeadlines: () => void;
  toggleModal: () => void;
}
interface Data {
  cssVariables: Record<string, any>;
  root: any;
  data: Record<string, any>;
  domain: string;
  limit: number;
  settings: Record<string, any>;
  scrollImageTop: number;
  tagFilterArray: number[];
  isModalVisible: boolean;
}

interface Computed {
  categoryAdditionalData: Record<string, any>;
  cssVars: object;
  headlines: HeadlinesData[];
  pager: QueryResult['pager'];
}

export default Vue.extend<Data, Methods, Computed, unknown>({
  components: {
    HeadlineBlocks,
    ArticlesCounter,
    ArticleHandler,
    Modal,
  },
  data() {
    const { domain } = this.$channelConfig('settings');
    return {
      root: null,
      cssVariables: {},
      data: {
        items: [],
      },
      domain,
      limit: 30,
      settings: {
        channel: false,
        category: false,
        lead: false,
        publishDate: false,
        author: false,
        titleComment: true,
        slidesPerBreakpoint: [1.5, 2, 4],
        effect: 'none',
      },
      scrollImageTop: 0,
      tagFilterArray: [],
      isModalVisible: false,
    };
  },
  computed: {
    cssVars() {
      return {};
    },
    categoryAdditionalData() {
      let categoryAdditionalData = {};

      if (this.$route && this.$route.params.category) {
        const categorySlug = this.$route.params.subcategory || this.$route.params.category;
        const customCategorySettings = this.$channelConfig('page').category?.component?.header;
        if (customCategorySettings && Array.isArray(customCategorySettings)) {
          const categoryAdditionalSettings = customCategorySettings.find((item: { categories: string | string[] }) => item.categories.includes(categorySlug));
          categoryAdditionalData = categoryAdditionalSettings?.additionalData || {};
        }
      }
      return categoryAdditionalData;
    },
    headlines() {
      const headlines = this.$store.getters['article/getMultimedijaArticles'];
      return headlines && headlines.items ? headlines.items.map((headline: HeadlinesData) => this.getHeadline(headline)) : [];
    },
    pager() {
      const headlines = this.$store.getters['article/getMultimedijaArticles'];
      return headlines && headlines.pager ? headlines.pager : { count: 0, limit: 0, offset: 0 };
    },
  },
  watch: {
    cssVariables: {
      handler() {
        this.$nextTick(() => {
          if (Object.keys(this.cssVariables).length !== 0) {
            Object.keys(this.cssVariables).forEach((key) => {
              if (this.root) {
                this.root.style.setProperty(key, this?.cssVariables[key]);
              }
            });
          }
        });
      },
      immediate: true,
    },
    tagFilterArray: {
      handler() {
        this.$nextTick(() => {
          if (this.headlines !== null) {
            this.updateHeadlines();
          }
        });
      },
      deep: true,
    },
  },
  created() {
    if (this.categoryAdditionalData.cssVariables) {
      this.cssVariables = this.categoryAdditionalData.cssVariables;
    }
  },
  mounted() {
    if (process.client) {
      this.root = document.documentElement;
      window.addEventListener('scroll', this.handleScroll);
    }
  },
  methods: {
    getHeadline(headline) {
      const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;
      return getHeadlineWithHref({ headline, domain: this.domain, fakeDomainEnabled });
    },
    handleScroll() {
      if (process.client) {
        const windowHeight = window.innerHeight;
        const windowScrollTop = window.scrollY;
        let x = null;
        x = 3000 - windowScrollTop;
        let imageOfset = (windowHeight - x) / 10;
        imageOfset = imageOfset > windowHeight ? imageOfset - windowHeight * Math.floor(imageOfset / windowHeight) : imageOfset;
        this.scrollImageTop = imageOfset;
      }
    },
    updateFitler(tagId) {
      if (!this.tagFilterArray.includes(tagId)) {
        this.tagFilterArray.push(tagId);
      } else {
        this.tagFilterArray.splice(this.tagFilterArray.indexOf(tagId), 1);
      }
    },
    toggleModal() {
      this.$nextTick(() => {
        this.isModalVisible = !this.isModalVisible;
      });
    },

    async updateHeadlines() {
      const activeArticle = this.$store.getters['article/getActiveArticle'];

      if (!activeArticle) {
        return;
      }

      const activeArticleCategoryId = activeArticle?.primaryCategory.id;
      const universalHeadlinesService = new UniversalHeadlinesService();
      const filteredHeadlines = await universalHeadlinesService.fetch({
        domain: this.domain,
        categoryId: activeArticleCategoryId,
        tagId: this.tagFilterArray,
        limit: this.limit,
        getCount: true,
        lite: true,
      });
      this.$store.commit('article/setMultimedijaArticles', filteredHeadlines);
      const filteredHeadlinesIds = filteredHeadlines.items.map((item) => item.id);
      this.$store.commit('article/setCrArticles', filteredHeadlinesIds.slice(1));
      this.$emit('resetArticles', [filteredHeadlinesIds[0].toString()]);
    },
  },
});
